import { cn } from '@april9au/react-common/utils';

import type { KeyTextField } from '@prismicio/client';
import { formatScore } from '@/chat/scoring';
import { Stars } from '@/components/stars';
import { Icon } from '@/components/ui/icon';
import { useRootLoaderData } from '@/hooks/use-root-loader-data';
import { formatString } from '@/i18n/catalogue';
import { useStrings } from '@/i18n/use-strings';
import { PlainText } from './rich-text';

/**
 * The end of a module, in the thread rather than on a new screen: the result,
 * a recap of the key takeaways and what is up next.
 *
 * The way on is the chat footer's button, pinned in the thumb zone rather
 * than on a card that may have scrolled away. See `ChatModule`.
 */

/** Five stars with the number beside them — motion is never the only cue. */
export function StarRating({ score, className }: { score: number; className?: string }) {
  const { completion } = useStrings();
  const { language } = useRootLoaderData();
  const value = formatScore(score, language);

  return (
    <p className={cn('flex items-center gap-1.5', className)}>
      <Stars score={score} className="text-xl" />
      {/* Visible for sighted readers; the sr-only line says the same with "stars". */}
      <span aria-hidden className="text-chat-ink text-sm font-bold tabular-nums">
        {formatString(completion.completion_score, { score: value })}
      </span>
      <span className="sr-only">{formatString(completion.completion_stars_label, { score: value })}</span>
    </p>
  );
}

/** The recap card `key_takeaways` renders, reused inside the completion card. */
export function TakeawaysCard({ heading, points }: { heading: KeyTextField; points: Array<KeyTextField> }) {
  const { chat } = useStrings();

  return (
    <section className="bg-bubble-buddy border-surf-yellow w-full max-w-[92%] rounded-2xl rounded-es-md border-s-4 p-4 shadow-[0_1px_2px_rgb(16_24_64/0.06),0_4px_12px_-6px_rgb(16_24_64/0.14)]">
      <h2 className="text-chat-ink font-bebas text-xl leading-none tracking-wide uppercase">
        {heading === null || heading.trim() === '' ? chat.chat_takeaways_heading : heading}
      </h2>
      <ul className="mt-3 space-y-2">
        {points.map((point, index) => (
          <li key={index} className="text-chat-ink flex gap-2.5 text-[15px] leading-snug">
            <Icon name="passport/check-circle" className="text-answer-correct mt-0.5 size-4 shrink-0" />
            <PlainText field={point} />
          </li>
        ))}
      </ul>
    </section>
  );
}

export type CompletionCardProps = {
  score: number;
  moduleIndex: number;
  moduleTotal: number;
  upNext: string | null;
  /** True when this module finishes the course. */
  courseComplete: boolean;
};

/** The record at the end of a module, with its own title and body on the last one. */
export function CompletionCard({ score, moduleIndex, moduleTotal, upNext, courseComplete }: CompletionCardProps) {
  const { chat, completion } = useStrings();

  return (
    <section
      className={cn(
        'bg-bubble-buddy w-full rounded-2xl p-5 shadow-[0_1px_3px_rgb(16_24_64/0.08),0_12px_28px_-16px_rgb(16_24_64/0.3)]',
        courseComplete && 'ring-surf-yellow ring-2',
      )}
    >
      <p className="text-chat-ink-muted text-[11px] font-bold tracking-[0.14em] uppercase">
        {formatString(chat.chat_module_label, { current: moduleIndex, total: moduleTotal })}
      </p>
      <h2 className="text-chat-ink font-bebas mt-1 text-3xl leading-none tracking-wide uppercase">
        {courseComplete ? completion.completion_course_title : completion.completion_title}
      </h2>
      <p className="text-chat-ink mt-2 text-[15px]">
        {courseComplete ? completion.completion_course_body : completion.completion_body}
      </p>

      <StarRating score={score} className="mt-4" />

      {upNext === null || courseComplete ? null : (
        <p className="border-chat-hairline text-chat-ink mt-4 border-t pt-4 text-sm font-semibold">
          {formatString(completion.completion_up_next, { title: upNext })}
        </p>
      )}
    </section>
  );
}
